import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useConnectionsContext } from '../chatConnections/connectionsContext';
import {
    CONNECTION_EVENTS,
    CONNECTION_STATES,
    SLACK_CONNECTION_ID,
    connectionEventProperties,
    trackConnectionEvent,
} from '../chatConnections/constants';
import useSlackLinkInvite from './hooks/useSlackLinkInvite';
import SlackConnectModal from './components/SlackConnectModal';
import SlackLinkConfirmModal from './components/SlackLinkConfirmModal';
import { SlackAgentContext } from './slackAgentContext';

const SlackAgentProvider = ({ children, translate }) => {
    const { connections, busyId, refresh, connect } = useConnectionsContext();
    const invite = useSlackLinkInvite(translate, refresh);
    const slack = connections.find(connection => connection.id === SLACK_CONNECTION_ID);
    const needsWorkspace = Boolean(slack?.available && slack.state === CONNECTION_STATES.NOT_CONNECTED);

    const [modalSurface, setModalSurface] = useState(null);

    useEffect(() => {
        // Coming back from Slack restores the page from bfcache with the explainer still open.
        const closeOnRestore = event => {
            if (event.persisted) setModalSurface(null);
        };
        window.addEventListener('pageshow', closeOnRestore);
        return () => window.removeEventListener('pageshow', closeOnRestore);
    }, []);

    const connectNow = useCallback(
        surface => {
            trackConnectionEvent(CONNECTION_EVENTS.INTRO_CONTINUED, connectionEventProperties(slack, surface));
            connect(SLACK_CONNECTION_ID, surface);
        },
        [connect, slack]
    );

    const openConnect = useCallback(
        surface => {
            trackConnectionEvent(CONNECTION_EVENTS.CONNECT_CLICKED, connectionEventProperties(slack, surface));
            trackConnectionEvent(CONNECTION_EVENTS.INTRO_OPENED, connectionEventProperties(slack, surface));
            setModalSurface(surface);
        },
        [slack]
    );

    const cancelConnect = useCallback(() => {
        trackConnectionEvent(CONNECTION_EVENTS.INTRO_CANCELLED, connectionEventProperties(slack, modalSurface));
        setModalSurface(null);
    }, [modalSurface, slack]);

    const value = useMemo(() => ({ needsWorkspace, openConnect }), [needsWorkspace, openConnect]);

    return (
        <SlackAgentContext.Provider value={value}>
            {children}
            <SlackConnectModal
                open={modalSurface != null}
                busy={busyId === SLACK_CONNECTION_ID}
                onContinue={() => connectNow(modalSurface)}
                onCancel={cancelConnect}
            />
            <SlackLinkConfirmModal
                pendingLink={invite.pendingLink}
                busy={invite.busy}
                onAccept={invite.acceptLink}
                onReject={invite.rejectLink}
            />
        </SlackAgentContext.Provider>
    );
};

SlackAgentProvider.propTypes = {
    children: PropTypes.node,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SlackAgentProvider);
